import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, useTheme } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import GroupsIcon from '@mui/icons-material/Groups';
import PaidIcon from '@mui/icons-material/Paid';

import api from '../../utils/functions/api';
import DetailSkeleton from './monthlySalarySummary/DetailSkeleton';
import EmployeeGroupTable from './monthlySalarySummary/EmployeeGroupTable';
import MonthlyAdditionTable from './monthlySalarySummary/MonthlyAdditionTable';
import NoCheckinTable from './monthlySalarySummary/NoCheckinTable';
import StatTile from './monthlySalarySummary/StatTile';
import { computeSummaryStats, formatCurrency } from './monthlySalarySummary/utils';

const EMPTY_DETAIL = { daily: [], fulltime: [], noPay: [], monthlyAdditions: [] };

export default function HrMonthlySalarySummaryDetail({ year, month }) {
  const theme = useTheme();
  const [data, setData] = useState(EMPTY_DETAIL);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const { data: result } = await api.get(
          `${process.env.REACT_APP_API_URL}/monthly-salary-summary/${year}/${month}`
        );
        setData(result || EMPTY_DETAIL);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [year, month]);

  const stats = useMemo(() => computeSummaryStats(data), [data]);

  if (loading) {
    return <DetailSkeleton />;
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile icon={<GroupsIcon fontSize="small" />} label="จ่ายเงินเดือนแล้ว" value={`${stats.totalPeople} คน`} color={theme.palette.primary.main} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile icon={<PaidIcon fontSize="small" />} label="ยอดรวมทั้งเดือน" value={formatCurrency(stats.totalAmount)} color={theme.palette.success.main} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<EventBusyIcon fontSize="small" />}
            label="ไม่ได้ลงเวลา"
            value={`${stats.noPayCount} คน`}
            color={theme.palette.error.main}
            muted={stats.noPayCount === 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<BadgeIcon fontSize="small" />}
            label="ยังไม่มีเลขบัตรประชาชน"
            value={`${stats.missingIdCount} คน`}
            color={theme.palette.warning.main}
            muted={stats.missingIdCount === 0}
          />
        </Grid>
      </Grid>

      <EmployeeGroupTable title="พนักงานรายวัน" rows={data.daily} accentColor={theme.palette.primary.main} />
      <EmployeeGroupTable title="พนักงานรายเดือน" rows={data.fulltime} accentColor={theme.palette.secondary.main} />
      <NoCheckinTable rows={data.noPay} accentColor={theme.palette.error.main} />
      <MonthlyAdditionTable rows={data.monthlyAdditions} accentColor={theme.palette.warning.main} />
    </Box>
  );
}
